import { ActionIcon, Badge, Button, Card, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconExternalLink, IconUnlink } from '@tabler/icons-react';
import Link from 'next/link';
import { useProjectId } from '~/hooks/context/useProjectId';
import { api } from '~/trpc-extra/api';
import FindIssueDialog from '../issue/FindIssueDialog';
import IssueTypeBadge from '../issue/IssueTypeBadge';

export default function TestRunAttemptIssueDialog({ attemptId, runId }: { attemptId: string; runId: string }) {
  const { data: issues } = api.testRun.getIssues.useQuery({ id: { attemptId } });

  const projectId = useProjectId();
  const trpcUtils = api.useUtils();

  const detachTestRunToIssues = api.issues.detachTestRunToIssues.useMutation({
    onSuccess: () => {
      trpcUtils.testRun.getIssues.invalidate().catch(() => undefined);
      notifications.show({
        message: 'Detached from issues',
        color: 'blue',
      });
    },
  });

  return (
    <div>
      <ul>
        {issues?.map((issue) => (
          <li
            key={issue.id}
            className="py-2"
          >
            <Card withBorder>
              <div className="flex justify-between">
                <div>
                  <div className="flex space-x-2">
                    <Link
                      href={`/project/${projectId}/issues/${issue.id}`}
                      target="_blank"
                      className="font-medium"
                    >
                      {issue.title}
                    </Link>
                    <IssueTypeBadge
                      issueType={issue.issueType}
                      isResolved={issue.status === 'RESOLVED'}
                    />
                    {issue.status === 'RESOLVED' && <Badge color="gray">Resolved</Badge>}
                  </div>
                  <div className="mt-1 text-stone-700 dark:text-stone-400">{issue.description}</div>
                </div>
                <div className="flex space-x-2">
                  <Tooltip label="Open issue">
                    <ActionIcon
                      variant="transparent"
                      size="sm"
                      autoContrast
                      className="text-stone-800 dark:text-stone-200"
                      onClick={() => {
                        window.open(`/project/${projectId}/issues/${issue.id}`, '_blank');
                      }}
                    >
                      <IconExternalLink />
                    </ActionIcon>
                  </Tooltip>

                  <Tooltip label="Unlink issue">
                    <ActionIcon
                      variant="transparent"
                      size="sm"
                      autoContrast
                      className="text-stone-800 dark:text-stone-200"
                      onClick={() => {
                        detachTestRunToIssues.mutate({
                          testRunAttemptId: attemptId,
                          issueIds: [issue.id],
                        });
                      }}
                    >
                      <IconUnlink />
                    </ActionIcon>
                  </Tooltip>
                </div>
              </div>
            </Card>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex justify-end">
        <Button
          variant="outline"
          onClick={() => {
            modals.open({
              title: 'Connect to Issue Tracker',
              size: 'xl',
              zIndex: 310,
              children: (
                <FindIssueDialog
                  runId={runId}
                  runAttemptId={attemptId}
                  closeDialog={() => modals.closeAll()}
                />
              ),
            });
          }}
        >
          + Connect Issues
        </Button>
      </div>
    </div>
  );
}
